import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import fetchWithAuth from "../fetchWithAuth";

const stages = { parsing: "Сбор выдачи", statistics: "Формирование статистики", schedule: "Генерация расписания", done: "Обработка завершена" };
const statuses = { queued: "в очереди", waiting: "ожидание", running: "выполняется", error: "ошибка", cancelled: "остановлено", done: "готово" };
const format = value => new Date(value).toLocaleString("ru-RU", { timeZone: "Europe/Moscow", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function ProjectProcessing({ projectId, onComplete }) {
  const [runs, setRuns] = useState([]);
  const [retrying, setRetrying] = useState(null);
  const [retryError, setRetryError] = useState("");
  const previous = useRef(new Map());
  const complete = useRef(onComplete);
  complete.current = onComplete;
  const retry = async run => {
    const id = run.projectId;
    setRetrying(id);
    setRetryError("");
    try {
      const response = await fetchWithAuth(run.reason === "rebuild" ? "/api/projects/bulk-action" : run.reason === "statistics" ? "/api/arsenkin/statistics" : "/api/arsenkin/parse", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(run.reason === "rebuild" ? { action: "rebuild", projectIds: [id] } : { projectId: id }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Не удалось повторить обработку");
      setRuns(current => current.map(run => run.projectId === id ? { ...run, status: "queued", stage: "parsing", error: null } : run));
    } catch (error) { setRetryError(error.message); }
    finally { setRetrying(null); }
  };
  useEffect(() => {
    let active = true;
    previous.current = new Map();
    const load = async () => {
      try {
        const response = await fetchWithAuth(`/api/project-runs${projectId ? `?projectId=${projectId}` : ""}`);
        if (response.ok) {
          const data = await response.json();
          if (active) {
            const finished = data.some(run => run.status === "done" && previous.current.has(run.id) && previous.current.get(run.id) !== `${run.revision}:done`);
            previous.current = new Map(data.map(run => [run.id, `${run.revision}:${run.status}`]));
            setRuns(data);
            if (finished) complete.current?.();
          }
        }
      } catch (error) { console.error("Ошибка загрузки обработки проектов", error); }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [projectId]);
  const visible = projectId ? runs : runs.filter(run => (run.project.isActive || run.reason === "statistics") && ["queued", "running", "waiting", "error"].includes(run.status));
  if (!visible.length) return null;
  return <div className="rounded-lg border bg-white p-4 space-y-3" aria-label="Обработка проектов">
    {visible.map(run => <div key={run.id} className="text-sm">
      <p className="font-semibold">{!projectId && <><Link to={`/project/${run.projectId}`} className="text-primary hover:underline">{run.project.name}</Link>: </>}
        {run.reason === "statistics" && run.stage === "done" ? "Статистика обновлена" : run.reason === "rebuild" && run.stage === "parsing" ? "Сбор выдачи для пересбора расписания" : stages[run.stage] || run.stage} — {statuses[run.status] || run.status}</p>
      <p className="text-gray-500 mt-1">{run.reason === "statistics" ? "Ручной сбор выдачи и статистики для неактивного проекта." : <>Расписание: {format(run.periodStart)} — {format(run.periodEnd)} МСК{run.fallback ? "; использованы предыдущие данные" : ""}</>}</p>
      {run.error && <p className="text-red-700 mt-1">{run.error}</p>}
      {run.status === "error" && (run.project.isActive || run.reason === "statistics") && <button className="text-primary underline mt-2 disabled:opacity-50" type="button" disabled={retrying !== null} onClick={() => retry(run)}>
        {retrying === run.projectId ? "Повторный запуск…" : "Повторить обработку"}
      </button>}
    </div>)}
    {retryError && <p role="alert" className="text-sm text-red-700">{retryError}</p>}
  </div>;
}
